import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { guestAPI } from '../services/api';

const STORAGE_KEY = 'rps_guest_session';

const GuestAuthContext = createContext(null);

/**
 * Guest phone-verification session context.
 *
 * Not real user auth — just a lead-capture gate: once a visitor verifies
 * their phone via SMS OTP, we drop a signed token in localStorage that
 * lasts 30 days. Every route on the site checks `isVerified` and, if false,
 * shows the entry modal.
 */
export const GuestAuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      setReady(true);
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setReady(true);
      return;
    }
    if (parsed.expires_at && new Date(parsed.expires_at) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      setReady(true);
      return;
    }
    // Trust the local expiry; skip round-trip so preview loads fast even when
    // backend is briefly down. Server will still reject the token elsewhere
    // if it was invalidated.
    setSession(parsed);
    setReady(true);
    // Optional: refresh in background to catch server-side invalidation.
    // Only wipe on an explicit 401/403 — never on network/5xx errors, or a
    // brief backend hiccup would kick every verified visitor back to the gate
    // and force another paid SMS.
    guestAPI.checkSession(parsed.token).catch((err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(STORAGE_KEY);
        setSession(null);
      }
    });
  }, []);

  const saveSession = useCallback((data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setSession(data);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }, []);

  const value = {
    session,
    isVerified: !!session,
    ready,
    saveSession,
    clearSession,
  };

  return (
    <GuestAuthContext.Provider value={value}>{children}</GuestAuthContext.Provider>
  );
};

export const useGuestAuth = () => {
  const ctx = useContext(GuestAuthContext);
  if (!ctx) throw new Error('useGuestAuth must be used inside GuestAuthProvider');
  return ctx;
};
